import { Box, InputAdornment, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

const inchesToParts = (
	totalInches: number | null | undefined,
): { feet: string; inches: string } => {
	if (totalInches === null || totalInches === undefined)
		return { feet: "", inches: "" };
	const abs = Math.abs(Math.round(totalInches));
	return {
		feet: String(Math.floor(abs / 12)),
		inches: String(abs % 12),
	};
};

const partsToInches = (feet: string, inches: string): number | null => {
	if (feet === "" && inches === "") return null;
	const f = feet === "" ? 0 : Number.parseInt(feet, 10);
	const i = inches === "" ? 0 : Number.parseInt(inches, 10);
	if (Number.isNaN(f) || Number.isNaN(i)) return null;
	return f * 12 + i;
};

interface Props {
	value: number | null | undefined;
	onChange?: (value: number | null) => void;
	disabled?: boolean;
}

export const FeetInchesInput = ({
	value,
	onChange = () => {},
	disabled = false,
}: Props) => {
	const feetRef = useRef<HTMLInputElement>(null);
	const inchesRef = useRef<HTMLInputElement>(null);

	const [feetDisplay, setFeetDisplay] = useState("");
	const [inchesDisplay, setInchesDisplay] = useState("");
	const isEditingRef = useRef(false);

	const handleFeet = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/[^0-9]/g, "");
		if (raw.length > 3) return;
		isEditingRef.current = true;
		setFeetDisplay(raw);
		onChange(partsToInches(raw, inchesDisplay));
	};

	const handleInches = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/[^0-9]/g, "");
		if (Number(raw) > 11) return;
		isEditingRef.current = true;
		setInchesDisplay(raw);
		onChange(partsToInches(feetDisplay, raw));
	};

	const normalize = () => {
		const total = partsToInches(feetDisplay, inchesDisplay);
		if (total === null) {
			setFeetDisplay("");
			setInchesDisplay("");
			return;
		}
		const parts = inchesToParts(total);
		setFeetDisplay(parts.feet);
		setInchesDisplay(parts.inches);
	};

	const onSegmentBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const related = e.relatedTarget as HTMLElement | null;
		if (related === feetRef.current || related === inchesRef.current) return;
		isEditingRef.current = false;
		normalize();
	};

	const onFeetKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "'" || e.key === " " || (e.key === "Tab" && !e.shiftKey)) {
			e.preventDefault();
			inchesRef.current?.focus();
		}
	};

	useEffect(() => {
		if (isEditingRef.current) return;
		const parts = inchesToParts(value);
		setFeetDisplay(parts.feet);
		setInchesDisplay(parts.inches);
	}, [value]);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				width: "100%",
				minWidth: 0,
			}}
		>
			<TextField
				inputRef={feetRef}
				size="small"
				margin="none"
				value={feetDisplay}
				onChange={handleFeet}
				onBlur={onSegmentBlur}
				onKeyDown={onFeetKeyDown}
				onFocus={(e) => {
					isEditingRef.current = true;
					e.target.select();
				}}
				placeholder="0"
				disabled={disabled}
				inputProps={{
					inputMode: "numeric" as const,
					style: { textAlign: "right" as const },
					"aria-label": "feet",
				}}
				InputProps={{
					endAdornment: <InputAdornment position="end">'</InputAdornment>,
				}}
				sx={{ flex: 1, minWidth: 0, backgroundColor: "white" }}
			/>

			<TextField
				inputRef={inchesRef}
				size="small"
				margin="none"
				value={inchesDisplay}
				onChange={handleInches}
				onBlur={onSegmentBlur}
				onFocus={(e) => {
					isEditingRef.current = true;
					e.target.select();
				}}
				placeholder="0"
				disabled={disabled}
				inputProps={{
					inputMode: "numeric" as const,
					style: { textAlign: "right" as const },
					"aria-label": "inches",
				}}
				InputProps={{
					endAdornment: <InputAdornment position="end">"</InputAdornment>,
				}}
				sx={{ flex: 1, minWidth: 0, backgroundColor: "white" }}
			/>
		</Box>
	);
};
